import React, { useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { WHOLESALE_COMMODITIES, FREQUENCIES, INCOTERMS, BUYER_TYPES, MIXED_CASE, MIXED_CASE_SIZE, describeMix } from "../../mock";
import { api, errorMessage } from "../../lib/api";
import Stepper, { STEPS } from "./Stepper";
import StepCommodities from "./StepCommodities";
import StepVolumes from "./StepVolumes";
import StepDetails from "./StepDetails";
import StepReview from "./StepReview";
import Confirmation from "./Confirmation";

const emptyLine = (c) => ({ grade: c.grades[0], volume: "", unit: c.units[0], frequency: FREQUENCIES[0], mix: {} });
const isMixed = (line) => line.grade === MIXED_CASE;
const mixTotal = (line) => Object.values(line.mix || {}).reduce((s, n) => s + n, 0);
const gradeLabel = (line) => (isMixed(line) ? describeMix(line.mix) : line.grade);
const emptyDetails = { company: "", name: "", email: "", phone: "", country: "", port: "", buyerType: BUYER_TYPES[0], incoterm: INCOTERMS[0], notes: "" };

export default function WholesaleWizard({ initial = [], sharedMix = null }) {
  const [step, setStep] = useState(sharedMix ? 1 : 0);
  const [selected, setSelected] = useState(initial);
  const [lines, setLines] = useState(() => {
    const base = Object.fromEntries(WHOLESALE_COMMODITIES.map((c) => [c.id, emptyLine(c)]));
    if (sharedMix) base.wine = { ...base.wine, grade: MIXED_CASE, unit: "cases of 6", mix: sharedMix };
    return base;
  });
  const [details, setDetails] = useState(emptyDetails);
  const [reference, setReference] = useState(null);
  const [busy, setBusy] = useState(false);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const validate = () => {
    if (step === 0 && selected.length === 0) return "Select at least one commodity to continue.";
    if (step === 1) {
      const mixed = selected.find((id) => isMixed(lines[id]) && mixTotal(lines[id]) !== MIXED_CASE_SIZE);
      if (mixed) return `Your mixed tasting case needs exactly ${MIXED_CASE_SIZE} bottles.`;
      const bad = selected.find((id) => !(Number(lines[id].volume) > 0));
      if (bad) return `Add an indicative volume for ${WHOLESALE_COMMODITIES.find((c) => c.id === bad).name}.`;
    }
    if (step === 2) {
      if (!details.company || !details.name || !details.country) return "Company, contact name and destination country are required.";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(details.email)) return "Please enter a valid email address.";
    }
    return null;
  };

  const next = () => {
    const err = validate();
    if (err) return toast.error(err);
    setStep((s) => s + 1);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/enquiries/wholesale", {
        lines: selected.map((id) => ({ commodity: id, grade: gradeLabel(lines[id]), volume: Number(lines[id].volume), unit: lines[id].unit, frequency: lines[id].frequency, mix: isMixed(lines[id]) ? lines[id].mix : null })),
        details: { company: details.company, name: details.name, email: details.email, phone: details.phone || null, country: details.country, port: details.port || null, buyer_type: details.buyerType, incoterm: details.incoterm, notes: details.notes || null }
      });
      setReference(data.reference);
      toast.success("Wholesale enquiry sent — our trade desk will be in touch.");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setStep(0);
    setSelected([]);
    setLines(Object.fromEntries(WHOLESALE_COMMODITIES.map((c) => [c.id, emptyLine(c)])));
    setDetails(emptyDetails);
    setReference(null);
  };

  return (
    <div data-testid="wholesale-wizard">
      <div className="bg-[#10271a] text-[#f6f1e7] rounded-t-[2rem] px-6 md:px-10 py-6 flex items-center justify-between gap-6">
        <Stepper current={reference ? STEPS.length : step} />
        <span className="text-[11px] tracking-[0.2em] uppercase text-[#f6f1e7]/60 shrink-0 hidden sm:block">Step {Math.min(step + 1, STEPS.length)} of {STEPS.length}</span>
      </div>

      <div className="bg-[#f6f1e7] rounded-b-[2rem] px-6 md:px-10 py-10 md:py-14">
        {reference ? (
          <Confirmation reference={reference} onReset={reset} />
        ) : (
          <>
            {step === 0 && <StepCommodities selected={selected} onToggle={toggle} />}
            {step === 1 && <StepVolumes selected={selected} lines={lines} onChange={(id, line) => setLines((l) => ({ ...l, [id]: line }))} />}
            {step === 2 && <StepDetails details={details} onChange={setDetails} />}
            {step === 3 && <StepReview selected={selected} lines={Object.fromEntries(Object.entries(lines).map(([id, l]) => [id, { ...l, grade: gradeLabel(l) }]))} details={details} />}

            <div className="mt-12 pt-8 border-t border-[#10271a]/10 flex items-center justify-between">
              <button
                type="button"
                onClick={() => setStep((s) => s - 1)}
                disabled={step === 0}
                data-testid="wizard-back"
                className="inline-flex items-center gap-2 text-sm tracking-[0.12em] uppercase font-semibold text-[#1f4a33] disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ArrowLeft size={16} /> Back
              </button>
              {step < STEPS.length - 1 ? (
                <button type="button" onClick={next} data-testid="wizard-next" className="group inline-flex items-center gap-3 bg-[#1f4a33] text-[#f6f1e7] px-7 py-4 rounded-full text-sm tracking-[0.12em] uppercase font-semibold hover:bg-[#10271a] transition-colors">
                  Continue <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                </button>
              ) : (
                <button type="button" onClick={submit} disabled={busy} data-testid="wizard-submit" className="group inline-flex items-center gap-3 bg-[#1f4a33] text-[#f6f1e7] px-7 py-4 rounded-full text-sm tracking-[0.12em] uppercase font-semibold hover:bg-[#10271a] transition-colors disabled:opacity-60">
                  {busy ? "Sending..." : "Send enquiry"} {!busy && <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
